import React, { useState, useMemo, useCallback, memo, useEffect } from "react";
import {
  Button,
  Input,
  Card,
  Spin,
  Pagination,
  Badge,
  Modal,
  Tooltip,
  Tag,
  Tabs,
  Drawer,
  Avatar,
  message,
  Collapse,
  Select,
  DatePicker,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
  MenuOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  FileTextOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { FaFileInvoiceDollar, FaPaperclip, FaUser } from "react-icons/fa";
import dayjs from "dayjs";
import {
  useSearchPatientSessions,
  useUpdateSessionStatus,
  SESSION_STATUS,
  SESSION_STATUS_CONFIG,
} from "../../../hooks/usePatientSessions";
import TextArea from "antd/es/input/TextArea";
import ConsultantAssignmentModal from "./ConsultantAssignmentModal";
import { getUserReport } from "../../../Api/getUserReport";
import { getImg } from "../../../Api/getImg";
import PatientInvoiceHistoryModal from "./PatientHistoryInvoiceModal";
import { FaUserDoctor } from "react-icons/fa6";
import ReassignSpecialistModal from "../../Modals/ReasignToSpecialist";
import drEasyApi from "../../../Api/Customapi";
import ReportViewModal from "../BookingsScreen/ReportViewModal";
import InvoiceModal from "../BookingsScreen/InvoiceModal";

const { TabPane } = Tabs;
    const role = localStorage.getItem("role");

const useResponsive = () => {
  const [breakpoint, setBreakpoint] = useState("lg");
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setBreakpoint("sm");
        setIsMobile(true);
        setIsTablet(false);
      } else if (width < 768) {
        setBreakpoint("md");
        setIsMobile(false);
        setIsTablet(true);
      } else if (width < 1024) {
        setBreakpoint("lg");
        setIsMobile(false);
        setIsTablet(true);
      } else if (width < 1280) {
        setBreakpoint("xl");
        setIsMobile(false);
        setIsTablet(false);
      } else {
        setBreakpoint("2xl");
        setIsMobile(false);
        setIsTablet(false);
      }
    };

    updateBreakpoint();
    window.addEventListener("resize", updateBreakpoint);
    return () => window.removeEventListener("resize", updateBreakpoint);
  }, []);

  return { breakpoint, isMobile, isTablet };
};
const SessionCard = memo(
  ({
    session,
    index,
    onUpdateStatus,
    onAssignConsultant,
    onViewReport,
    isMobile,
    onViewInvoiceHistory,
    onReassign,
    setReassignModalVisible,
    onInvoiceClick,
  }) => {
    const status =
      SESSION_STATUS_CONFIG[session.status] || SESSION_STATUS_CONFIG[0];
    const sessionDate = dayjs(session.sessionDate);
    return (
      <div
        className={`group relative bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border ${status.borderColor
          } overflow-hidden ${isMobile ? "mb-4" : ""}`}
        style={{
          animationDelay: `${index * 100}ms`,
          animation: "fadeInUp 0.6s ease-out forwards",
        }}
      >
        <div className={`absolute top-2 right-2 sm:top-4 sm:right-4 z-10`}>
          <Badge
            status={status.color}
            text={
              <span
                className={`px-3 py-1 rounded-full text-xs font-bold ${status.bgLight} border ${status.borderColor} ${status.textColor} shadow-sm`}
              >
                {status.label}
              </span>
            }
          />
        </div>

        <div className="p-4 sm:p-6">
          {/* Specialist Info */}
          <div
            className={`flex items-center mb-3 sm:mb-4 pt-3 ${isMobile ? "flex-wrap" : ""
              }`}
          >
            <Avatar
              size={isMobile ? 40 : 48}
              src={
                session.specialistPhotoId
                  ? `/api/getImg/${session.specialistPhotoId}`
                  : null
              }
              icon={!session.specialistPhotoId ? <UserOutlined /> : null}
              className="bg-gradient-to-r from-primary to-primary_hover flex-shrink-0"
            />
            <div className="ml-3 sm:ml-4 min-w-0 flex-1 mt-3 ">
              <h3
                className={`font-bold text-gray-800 ${isMobile ? "text-base" : "text-lg"
                  } mb-1`}
              >
                {session.specialistName}
              </h3>
            </div>
            {session.status === SESSION_STATUS.PENDING && (
              <div className="flex gap-3 items-center ">
                <Tooltip title="Assign to Consultant">
                  <Button
                    type="primary"
                    icon={<FaUserDoctor />}
                    onClick={() => onAssignConsultant(session)}
                    size={isMobile ? "small" : "middle"}
                    className="rounded-lg bg-primary hover:bg-primary_hover border-none"
                  ></Button>
                </Tooltip>
                <Tooltip title="Reassign to another Specialist">
                  <Button
                    type="primary"
                    icon={<FaUser />}
                    onClick={() => onReassign(session)}  // ← was onAssignConsultant
                    size={isMobile ? "small" : "middle"}
                    className="rounded-lg bg-secondary hover:bg-secondary_hover border-none"
                  />
                </Tooltip>
              </div>)}
          </div>


          {/* Session Details */}
          <div
            className={`p-3 sm:p-4 rounded-lg sm:rounded-xl mb-3 sm:mb-4 bg-gray-50 border border-gray-200`}
          >
            <div className="flex items-center mb-2">
              <FaUser className="mr-2 text-gray-500" />
              <span
                className={`font-semibold text-gray-800 ${isMobile ? "text-sm" : "text-base"
                  }`}
              >
                {session.patientName}
              </span>
            </div>

            <div className="flex items-center text-xs sm:text-sm text-gray-600 mb-2">
              <ClockCircleOutlined className="mr-2 flex-shrink-0" />
              <span>
                {sessionDate.format("MMM D, YYYY")} -{" "}
                {dayjs(session.startTime, "HH:mm:ss").format("h:mm A")}
              </span>
            </div>

            {session.noteText ? (
              <div className="text-xs text-gray-600 mt-2">
                <span className="font-semibold">Note:</span> {session.noteText}
              </div>
            ) : (
              <div className="text-xs text-gray-600 mt-2">
                <span className="font-semibold">Note:</span> no notes yet
              </div>
            )}

            {session.cancelReason && (
              <div className="text-xs text-red-600 mt-2">
                <span className="font-semibold">Cancellation Reason:</span>{" "}
                {session.cancelReason}
              </div>
            )}
          </div>

          {/* Actions */}
          <div
            className={`flex items-center justify-between pt-3 sm:pt-4 border-t border-gray-100 ${isMobile ? "flex-wrap gap-2" : ""
              }`}
          >
            <div
              className={`flex ${isMobile ? "gap-2 flex-wrap" : "space-x-3"}`}
            >
              {session.status === SESSION_STATUS.PENDING && (
                <>

                  {/* <Tooltip title="View Report">
                    <Button
                      type="default"
                      icon={<FileTextOutlined />}
                      onClick={() => onViewReport(session)}
                      size={isMobile ? "small" : "middle"}
                      className="rounded-lg border-blue-500 text-blue-500 hover:bg-blue-50"
                    ></Button>
                  </Tooltip> */}
                  <Tooltip title={session.invoiceId ? "Edit Invoice" : "Create Invoice"}>
                    <Button
                      type="default"
                      icon={session.invoiceId ? <EditOutlined /> : <FaPaperclip />}
                      onClick={() => onInvoiceClick(session)}
                      size={isMobile ? "small" : "middle"}
                      className="rounded-lg border-primary text-primary hover:bg-primary/10"
                    />
                  </Tooltip>
                  <Tooltip title="Invoice History">
                    <Button
                      type="default"
                      icon={<FaFileInvoiceDollar />}
                      onClick={() => onViewInvoiceHistory(session)}
                      size={isMobile ? "small" : "middle"}
                      className="rounded-lg border-green-500 text-green-500 hover:bg-purple-50"
                    ></Button>
                  </Tooltip>
                  {role == 4 &&

                    <>
                    <Tooltip title="Mark as Complete">
                        <Button
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          onClick={() => onUpdateStatus(session, SESSION_STATUS.COMPLETED)}
                          size={isMobile ? 'small' : 'middle'}
                          className="rounded-lg bg-green-600 hover:bg-green-700 border-none"
                        >
                        </Button>
                      </Tooltip><Tooltip title="Mark as Skipped">
                        <Button
                          type=""
                          icon={<ReloadOutlined />}
                          onClick={() => onUpdateStatus(session, SESSION_STATUS.SKIPPED)}
                          size={isMobile ? 'small' : 'middle'}
                          className="rounded-lg bg-yellow-50 text-yellow-500"
                        >
                        </Button>
                      </Tooltip>
                    <Tooltip title="Cancel Session">
                      <Button
                        danger
                        icon={<CloseCircleOutlined />}
                        onClick={() => onUpdateStatus(session, SESSION_STATUS.CANCELLED)}
                        size={isMobile ? 'small' : 'middle'}
                        className="rounded-lg"
                      >
                      </Button>
                    </Tooltip>


                    </>}
                </>
              )}

              {/* {session.status === SESSION_STATUS.ACTIVE && (
                <Tooltip title="Mark as Completed">
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => onUpdateStatus(session, SESSION_STATUS.COMPLETED)}
                    size={isMobile ? 'small' : 'middle'}
                    className="rounded-lg bg-green-600 hover:bg-green-700 border-none"
                  >
                  </Button>
                </Tooltip>
              )} */}

              {session.status === SESSION_STATUS.COMPLETED && (

                <div>



                  <Tooltip title="View Order Report">
                    <Button
                      type="default"
                      icon={<FileTextOutlined />}
                      onClick={() => onViewReport(session)}
                      size={isMobile ? "small" : "middle"}
                      className="rounded-lg border-blue-500 text-blue-500 hover:bg-blue-50"
                    >View Order report</Button>
                  </Tooltip>
                </div>
              )}

              {session.status === SESSION_STATUS.CANCELLED && (
                <Tag color="red" className="text-xs">
                  ✗ Cancelled By {session.cancelledByName?session.cancelledByName:"No data provided"}
                </Tag>
              )}

              {session.status === SESSION_STATUS.SKIPPED && (
                <Tag color="default" className="text-xs">
                  ⊘ Skipped
                </Tag>
              )}
            </div>

            <div
              className={`text-xs text-gray-400 ${isMobile ? "mt-2 w-full text-center" : ""
                }`}
            >
              #{session.sessionId.slice(-4)}
            </div>
          </div>
        </div>

        <div className="absolute inset-0 bg-gradient-to-r from-primary_hover/5 to-cyan-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-xl sm:rounded-2xl"></div>
      </div>
    );
  },
);

SessionCard.displayName = "SessionCard";

const SpecialistSessionsScreen = () => {
  const { breakpoint, isMobile, isTablet } = useResponsive();
  const [activeTab, setActiveTab] = useState("0");
  const [filtersDrawerVisible, setFiltersDrawerVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [selectedNewStatus, setSelectedNewStatus] = useState(null);
  const [consultantModalVisible, setConsultantModalVisible] = useState(false);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [noteText, setNoteText] = useState("");
  const [additionalNote, setAdditionalNote] = useState("");
  const [invoiceHistoryModalVisible, setInvoiceHistoryModalVisible] = useState(false);
  const [selectedPatientForInvoices, setSelectedPatientForInvoices] = useState(null);
  const [reassignModalVisible, setReassignModalVisible] = useState(false);
  const [sessionToReassign, setSessionToReassign] = useState(null);
  const [patientSearchValue, setPatientSearchValue] = useState("");
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);
  const [specialistSearchValue, setSpecialistSearchValue] = useState("");
  const [specialistOptions, setSpecialistOptions] = useState([]);
  const [specialistSearchLoading, setSpecialistSearchLoading] = useState(false);
  const [reportViewModalVisible, setReportViewModalVisible] = useState(false);
  const [currentViewReportSession, setCurrentViewReportSession] = useState(null);
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [currentInvoiceSession, setCurrentInvoiceSession] = useState(null);
  const [doctorServices, setDoctorServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const handleSpecialistSearch = useCallback(async (value) => {
    setSpecialistSearchLoading(true);
    try {
      const res = await drEasyApi.get('/api/Centers/getMySpecialists', {
        params: { NameSearch: value || " ", PageNumber: 1, PageSize: 20 }
      });
      setSpecialistOptions(
        (res.data.specialists || []).map((s) => ({
          label: s.name || " ",
          value: s.specialistId,
        }))
      );
    } catch (e) {
      console.error(e);
    } finally {
      setSpecialistSearchLoading(false);
    }
  }, []);
  const handlePatientSearch = useCallback(async (value) => {
    setPatientSearchLoading(true);
    try {
      const res = await drEasyApi.get('/api/Centers/getPatients', {
        params: { NameFilter: value ? value : " ", Page: 1, PageSize: 20 }
      });
      setPatientOptions(
        (res.data.patients || []).map((p) => ({
          label: p.fullName || " ",
          value: p.accountId,
        }))
      );
    } catch (e) {
      console.error(e);
    } finally {
      setPatientSearchLoading(false);
    }
  }, []);
  const handleReassign = useCallback((session) => {
    setSessionToReassign(session);
    setReassignModalVisible(true);
  }, []);
  const fetchDoctorServices = useCallback(async () => {
    setServicesLoading(true);
    try {
      const response = await drEasyApi.get("/api/Services/GetMyServices");
      setDoctorServices(response.data);
    } catch {
      // message.error("Error fetching services");
    } finally {
      setServicesLoading(false);
    }
  }, []);

  const handleInvoiceClick = useCallback(async (session) => {
    setCurrentInvoiceSession(session);
    setInvoiceModalVisible(true);
    if(role!=7){

      await fetchDoctorServices();
    }
  }, [fetchDoctorServices]);

  const handleInvoiceModalClose = useCallback(() => {
    setInvoiceModalVisible(false);
    setCurrentInvoiceSession(null);
  }, []);
  const [filters, setFilters] = useState({
    specialistId: "",
    userId: "",
    fromDate: null,
    toDate: null,
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: isMobile ? 4 : isTablet ? 6 : 12,
    total: 0,
  });

  // الآن يمكن استخدام filters في الـ queries
  const { data: pendingData } = useSearchPatientSessions({
    ...filters,
    status: SESSION_STATUS.PENDING,
    pageNumber: 1,
    pageSize: 1,
  });

  const { data: cancelledData } = useSearchPatientSessions({
    ...filters,
    status: SESSION_STATUS.CANCELLED,
    pageNumber: 1,
    pageSize: 1,
  });

  const { data: completedData } = useSearchPatientSessions({
    ...filters,
    status: SESSION_STATUS.COMPLETED,
    pageNumber: 1,
    pageSize: 1,
  });

  const { data: skippedData } = useSearchPatientSessions({
    ...filters,
    status: SESSION_STATUS.SKIPPED,
    pageNumber: 1,
    pageSize: 1,
  });

  // Fetch sessions with React Query using SearchSessions endpoint
  const {
    data: sessionsData,
    isLoading,
    refetch,
  } = useSearchPatientSessions({
    specialistId: filters.specialistId || undefined,
    userId: filters.userId ? parseInt(filters.userId) : undefined,
    fromDate: filters.fromDate || undefined,
    toDate: filters.toDate || undefined,
    status: parseInt(activeTab),
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
  });

  const updateSessionMutation = useUpdateSessionStatus();

  useEffect(() => {
    if (sessionsData?.totalCount) {
      setPagination((prev) => ({ ...prev, total: sessionsData.totalCount }));
    }
  }, [sessionsData?.totalCount]);

  // Update statistics to use these counts
  const statistics = useMemo(() => {
    return {
      pending: pendingData?.totalCount || 0,
      cancelled: cancelledData?.totalCount || 0,
      completed: completedData?.totalCount || 0,
      skipped: skippedData?.totalCount || 0,
      total: sessionsData?.totalCount || 0,
    };
  }, [pendingData, cancelledData, completedData, skippedData, sessionsData]);

  // Group sessions by patientId
  const groupedSessions = useMemo(() => {
    if (!sessionsData?.sessions || sessionsData.sessions.length === 0)
      return {};

    return sessionsData.sessions.reduce((groups, session) => {
      const patientId = session.patientId || session.profileID;
      if (!groups[patientId]) {
        groups[patientId] = {
          patientName: session.patientName,
          patientId: patientId,
          sessions: [],
        };
      }
      groups[patientId].sessions.push(session);
      return groups;
    }, {});
  }, [sessionsData?.sessions]);



  // 3. Add handler for closing
  const handleReportViewModalClose = useCallback(() => {
    setReportViewModalVisible(false);
    setCurrentViewReportSession(null);
  }, []);
  const handleViewInvoiceHistory = useCallback((session) => {
    setSelectedPatientForInvoices({
      patientId: session.patientId,
      patientName: session.patientName,
    });
    setInvoiceHistoryModalVisible(true);
  }, []);

  const handleInvoiceHistoryClose = useCallback(() => {
    setInvoiceHistoryModalVisible(false);
    setSelectedPatientForInvoices(null);
  }, []);
  const handleAssignToConsultant = (session) => {
    setCurrentSession(session);
    setConsultantModalVisible(true);
  };

  const handleConsultantAssigned = (consultant) => {
    message.success(`Successfully assigned to Dr. ${consultant.name}`);
    // Refresh sessions
    refetch();
  };


  // Filters state


  useEffect(() => {
    if (sessionsData?.totalCount) {
      setPagination((prev) => ({ ...prev, total: sessionsData.totalCount }));
    }
  }, [sessionsData?.totalCount]);

  // Handlers
  const handleUpdateStatus = useCallback((session, newStatus) => {
    // These statuses don't need extra input — submit directly
    if (newStatus === SESSION_STATUS.COMPLETED || newStatus === SESSION_STATUS.SKIPPED) {
      updateSessionMutation.mutate(
        { sessionId: session.sessionId, newStatus },
        { onSuccess: () => refetch() }
      );
      return;
    }

    // CANCELLED and COMPLETED need the modal for extra input
    setCurrentSession(session);
    setSelectedNewStatus(newStatus);
    setStatusModalVisible(true);
    setCancelReason("");
    setNoteText("");
    setAdditionalNote("");
  }, [updateSessionMutation, refetch]);

  const handleStatusSubmit = useCallback(() => {
    // Validation
    if (
      selectedNewStatus === SESSION_STATUS.CANCELLED &&
      !cancelReason.trim()
    ) {
      message.error("Please provide a cancellation reason");
      return;
    }

    if (selectedNewStatus === SESSION_STATUS.COMPLETED && !noteText.trim()) {
      message.error("Please add session notes");
      return;
    }

    const payload = {
      sessionId: currentSession.sessionId,
      newStatus: selectedNewStatus,
      cancelReason:
        selectedNewStatus === SESSION_STATUS.CANCELLED
          ? cancelReason
          : undefined,
      noteText:
        selectedNewStatus === SESSION_STATUS.COMPLETED ? noteText : undefined,
      additionalNote:
        selectedNewStatus === SESSION_STATUS.COMPLETED && additionalNote
          ? additionalNote
          : undefined,
    };

    updateSessionMutation.mutate(payload, {
      onSuccess: () => {
        setStatusModalVisible(false);
        setCancelReason("");
        setNoteText("");
        setAdditionalNote("");
        refetch();
      },
    });
  }, [
    currentSession,
    selectedNewStatus,
    cancelReason,
    noteText,
    additionalNote,
    updateSessionMutation,
    refetch,
  ]);
  const handleResetFilters = useCallback(() => {
    setFilters({ specialistId: "", userId: "", fromDate: null, toDate: null });
    setPatientOptions([]);
    setSpecialistOptions([]);  // ← add this
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (isMobile) setFiltersDrawerVisible(false);
  }, [isMobile]);
  const getStatusModalTitle = () => {
    switch (selectedNewStatus) {
      case SESSION_STATUS.COMPLETED:
        return "Complete Session";
      case SESSION_STATUS.CANCELLED:
        return "Cancel Session";
      case SESSION_STATUS.SKIPPED:
        return "Skip Session";
      default:
        return "Update Session Status";
    }
  };
  const handleViewReport = useCallback((session) => {
    setCurrentViewReportSession(session);
    setReportViewModalVisible(true);
  }, []);

  const FiltersContent = useMemo(
    () => (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3
            className={`font-semibold text-gray-800 flex items-center m-0 ${isMobile ? "text-sm" : "text-md"
              }`}
          >
            <FilterOutlined className="mr-2 text-sm" />
            Search Filters
          </h3>
          {isMobile && (
            <Button
              icon={<MenuOutlined />}
              onClick={() => setFiltersDrawerVisible(false)}
              type="text"
              size="small"
            />
          )}
        </div>

        <div
          className={`grid grid-cols-1 ${isMobile
            ? "gap-3"
            : isTablet
              ? "md:grid-cols-2 gap-4"
              : "lg:grid-cols-2 xl:grid-cols-5 gap-4"
            }`}
        >
          {/* <Input
            placeholder="User ID (Number)"
            type="number"
            value={filters.userId}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, userId: e.target.value }))
            }
            allowClear
            size={isMobile ? "middle" : "large"}
            className="rounded-xl border-gray-200 hover:border-blue-400 focus:border-primary"
          /> */}
          <Select
            showSearch
            allowClear
            placeholder="Search patient by name..."
            filterOption={false}
            onSearch={handlePatientSearch}
            onFocus={() => {
              // جيب الكل لما يفتح لأول مرة
              if (patientOptions.length === 0) {
                handlePatientSearch("");
              }
            }}
            onChange={(value) => {
              setFilters((prev) => ({ ...prev, userId: value || "" }));
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            onClear={() => {
              setFilters((prev) => ({ ...prev, userId: "" }));
              setPatientOptions([]);
            }}
            loading={patientSearchLoading}
            options={patientOptions}
            size={isMobile ? "middle" : "large"}
            className="w-full rounded-xl"
          />
          <Select
            showSearch
            allowClear
            placeholder="Search specialist by name..."
            filterOption={false}
            onSearch={handleSpecialistSearch}
            onFocus={() => { if (specialistOptions.length == 0) { handleSpecialistSearch("") } }}
            onChange={(value) => {
              setFilters((prev) => ({ ...prev, specialistId: value || " " }));
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            onClear={() => {
              setFilters((prev) => ({ ...prev, specialistId: " " }));
              setSpecialistOptions([]);
            }}
            loading={specialistSearchLoading}
            options={specialistOptions}
            size={isMobile ? "middle" : "large"}
            className="w-full rounded-xl"
          />
       <DatePicker
  placeholder="From Date"
  format="YYYY-MM-DD"
  value={filters.fromDate ? dayjs(filters.fromDate) : null}
  onChange={(date) =>
    setFilters((prev) => ({
      ...prev,
      fromDate: date ? date.format("YYYY-MM-DD") : null,
    }))
  }
  size={isMobile ? "middle" : "large"}
  className="w-full rounded-xl border-gray-200 hover:border-blue-400 focus:border-primary"
/>

<DatePicker
  placeholder="To Date"
  format="YYYY-MM-DD"
  value={filters.toDate ? dayjs(filters.toDate) : null}
  onChange={(date) =>
    setFilters((prev) => ({
      ...prev,
      toDate: date ? date.format("YYYY-MM-DD") : null,
    }))
  }
  disabledDate={(current) =>
    filters.fromDate
      ? current && current < dayjs(filters.fromDate).startOf("day")
      : false
  }
  size={isMobile ? "middle" : "large"}
  className="w-full rounded-xl border-gray-200 hover:border-blue-400 focus:border-primary"
/>

          <Button
            icon={<ReloadOutlined />}
            onClick={handleResetFilters}
            size={isMobile ? "middle" : "large"}
            className="rounded-xl bg-primary border-none text-white hover:bg-primary_hover transform hover:scale-105 transition-all duration-300"
            block
          >
            Reset Filters
          </Button>
        </div>
      </div>
    ),
    [
      filters, isMobile, isTablet, handleResetFilters,
      patientOptions, patientSearchLoading, handlePatientSearch,
      specialistOptions, specialistSearchLoading, handleSpecialistSearch,  // ← add these 3
    ],);

  console.log(currentViewReportSession, "cu");

  return (
    <div className="min-h-screen p-2 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .ant-tabs-tab {
          border-width: 1px;
          border-style: solid;
          border-color: #298e9f !important;
          margin-right: ${isMobile ? "4px" : "8px"} !important;
          padding: ${isMobile ? "4px 8px" : "8px 16px"} !important;
          font-size: ${isMobile ? "12px" : "14px"} !important;
        }
      `}</style>

      <div >
        {/* Tabs */}
        <Card
          className={`mb-3 sm:mb-6 shadow-sm border-0 bg-white/80 backdrop-blur-sm ${isMobile ? "px-2" : ""
            }`}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            type="line"
            size={isMobile ? "small" : "large"}
            tabBarGutter={isMobile ? 4 : 8}
          >
            <TabPane tab={`⏳ Pending (${statistics.pending})`} key="0" />
            <TabPane tab={`❌ Cancelled (${statistics.cancelled})`} key="1" />
            <TabPane tab={`✅ Completed (${statistics.completed})`} key="2" />
          </Tabs>
        </Card>

        {/* Filters */}
        {isMobile ? (
          <>
            <div className="mb-3">
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFiltersDrawerVisible(true)}
                className="rounded-xl bg-primary border-none text-white w-full"
              >
                Search Filters
              </Button>
            </div>
            <Drawer
              title="Search Filters"
              placement="bottom"
              closable={true}
              onClose={() => setFiltersDrawerVisible(false)}
              open={filtersDrawerVisible}
              height="auto"
            >
              {FiltersContent}
            </Drawer>
          </>
        ) : (
          <Card className="mb-3 shadow-sm border-0 bg-white/80 backdrop-blur-sm">
            {FiltersContent}
          </Card>
        )}

        <Spin spinning={isLoading}>
          {Object.values(groupedSessions).length > 0 ? (
            <>

              <Collapse
                className="mb-6"
                style={{ border: 0 }}
                defaultActiveKey={[]}
                expandIconPosition="end"
                ghost={isMobile}
              >
                {Object.values(groupedSessions).map((group, groupIndex) => (
                  <Collapse.Panel
                    key={group.patientId}
                    header={
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center">
                          <Avatar
                            size={isMobile ? 32 : 40}
                            icon={<UserOutlined />}
                            className="bg-gradient-to-r from-primary to-primary_hover mr-3"
                          />
                          <div>
                            <span
                              className={`font-bold text-gray-800 ${isMobile ? "text-sm" : "text-lg"}`}
                            >
                              {group.patientName}
                            </span>
                            <div className="text-xs text-gray-500">
                              {group.sessions.length}{" "}
                              {group.sessions.length === 1
                                ? "session"
                                : "sessions"}
                            </div>
                          </div>
                        </div>
                        <Badge
                          count={group.sessions.length}
                          style={{ backgroundColor: "#298e9f" }}
                          className="mr-2"
                        />
                      </div>
                    }
                    className="mb-3 bg-white rounded-xl shadow-sm border border-gray-200"
                  >
                    <div
                      className={`grid grid-cols-1 ${isMobile
                        ? "gap-3 p-2"
                        : isTablet
                          ? "sm:grid-cols-2 gap-4"
                          : "sm:grid-cols-2 lg:grid-cols-2 gap-4"
                        }`}
                    >
                      {group.sessions.map((session, index) => (
                        <SessionCard
                          key={session.sessionId}
                          session={session}
                          index={index}
                          onUpdateStatus={handleUpdateStatus}
                          onAssignConsultant={handleAssignToConsultant}
                          onViewReport={handleViewReport}
                          onViewInvoiceHistory={handleViewInvoiceHistory}
                          isMobile={isMobile}
                          onReassign={handleReassign}
                          setReportModalVisible={setReportModalVisible}
                          onInvoiceClick={handleInvoiceClick}
                        />
                      ))}
                    </div>
                  </Collapse.Panel>
                ))}
              </Collapse>
              {/* Pagination */}
              <div className="flex justify-center">
                <div
                  className={`bg-white rounded-xl sm:rounded-2xl shadow-lg ${isMobile ? "p-2" : "p-4"
                    }`}
                >
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={(page, pageSize) => {
                      setPagination({
                        ...pagination,
                        current: page,
                        pageSize,
                      });
                    }}
                    showTotal={(total) => (
                      <span
                        className={`text-gray-600 font-medium ${isMobile ? "text-xs" : ""
                          }`}
                      >
                        {isMobile
                          ? `${total} total`
                          : `Total ${total} sessions`}
                      </span>
                    )}
                    size={isMobile ? "small" : "default"}
                    showSizeChanger={!isMobile}
                    showQuickJumper={!isMobile}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <div className={`${isMobile ? "text-4xl" : "text-6xl"} mb-4`}>
                📋
              </div>
              <h3
                className={`font-bold text-gray-600 mb-2 ${isMobile ? "text-lg" : "text-2xl"
                  }`}
              >
                No Sessions Found
              </h3>
              <p className={`text-gray-500 ${isMobile ? "text-sm" : ""}`}>
                Try adjusting your search filters
              </p>
            </div>
          )}
        </Spin>
      </div>

      {/* Update Status Modal */}
      <Modal
        title={getStatusModalTitle()}
        open={statusModalVisible}
        onOk={handleStatusSubmit}
        onCancel={() => {
          setStatusModalVisible(false);
          setCancelReason("");
          setNoteText("");
          setAdditionalNote("");
        }}
        okText="Cancel Booking"
        cancelText="Cancel "
        okButtonProps={{ style: { backgroundColor: "teal", color: "white" } }}
        confirmLoading={updateSessionMutation.isPending}
        width={isMobile ? "95vw" : 600}
        centered={isMobile}
      >
        <div>
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <p>
              <strong>Patient:</strong> {currentSession?.patientName}
            </p>
            <p>
              <strong>Specialist:</strong> {currentSession?.specialistName}
            </p>
            <p>
              <strong>Date:</strong>{" "}
              {currentSession?.sessionDate
                ? dayjs(currentSession.sessionDate).format("MMM D, YYYY")
                : "N/A"}
            </p>
          </div>

          {selectedNewStatus === SESSION_STATUS.CANCELLED && (
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Cancellation Reason <span className="text-red-500">*</span>
              </label>
              <TextArea
                rows={4}
                placeholder="Enter the reason for cancellation..."
                className="rounded-lg"
                value={cancelReason}
                minLength={10}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
          )}

          {selectedNewStatus === SESSION_STATUS.COMPLETED && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Session Notes <span className="text-red-500">*</span>
                </label>
                <TextArea
                  rows={4}
                  placeholder="Enter session notes..."
                  className="rounded-lg"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Additional Notes (Optional)
                </label>
                <TextArea
                  rows={3}
                  placeholder="Enter any additional notes..."
                  className="rounded-lg"
                  value={additionalNote}
                  onChange={(e) => setAdditionalNote(e.target.value)}
                />
              </div>
            </>
          )}

          {selectedNewStatus === SESSION_STATUS.SKIPPED && (
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-gray-700">
                This session will be marked as skipped. No additional
                information is required.
              </p>
            </div>
          )}
        </div>
      </Modal>
      <ReportViewModal
        visible={reportViewModalVisible}
        onCancel={handleReportViewModalClose}
        sessionId={currentViewReportSession?.sessionId}  // ← sessionId passed as bookingId
        booking={{
          patientName: currentViewReportSession?.patientName,
          clinicName: currentViewReportSession?.specialistName,
        }}
        isMobile={isMobile}
        showActions={false}  // ← hide booking-specific actions
      />
      <PatientInvoiceHistoryModal
        visible={invoiceHistoryModalVisible}
        onCancel={handleInvoiceHistoryClose}
        patientId={selectedPatientForInvoices?.patientId}
        patientName={selectedPatientForInvoices?.patientName}
        isMobile={isMobile}
        onEditInvoice={(invoice) => {
          // Handle edit invoice - you can open your InvoiceModal here
          console.log("Edit invoice:", invoice);
          handleInvoiceHistoryClose();
          // You can trigger your existing invoice modal here if needed
        }}
        onPayInvoice={(invoice) => {
          // Handle after payment success
          console.log("Payment processed for invoice:", invoice);
          refetch(); // Refresh sessions if needed
        }}
      />
      <ReassignSpecialistModal
        visible={reassignModalVisible}
        onCancel={() => { setReassignModalVisible(false); setSessionToReassign(null); }}
        session={sessionToReassign}
        isMobile={isMobile}
        onSuccess={() => { refetch(); setReassignModalVisible(false); setSessionToReassign(null); }}
      />
      <ConsultantAssignmentModal
        visible={consultantModalVisible}
        onCancel={() => setConsultantModalVisible(false)}
        onAssign={handleConsultantAssigned}
        session={currentSession}
        isMobile={isMobile}
      />
      <InvoiceModal
        visible={invoiceModalVisible}
        onCancel={handleInvoiceModalClose}
        booking={currentInvoiceSession?.invoiceId ? {
          patientId: currentInvoiceSession.patientId,
          invoiceId: currentInvoiceSession?.invoiceId,
          patientName: currentInvoiceSession?.patientName,
          bookingCode: currentInvoiceSession?.sessionId?.slice(-6) || "",

          clinicId: currentInvoiceSession?.clinicId,
        } : {
          clinicId: currentInvoiceSession?.clinicId,
          patientName: currentInvoiceSession?.patientName,
          bookingCode: currentInvoiceSession?.sessionId?.slice(-6) || "",
          patientId: currentInvoiceSession?.patientId,


        }}
        patient={currentInvoiceSession ? { accountId: currentInvoiceSession.patientId } : null}
        patientSessionId={currentInvoiceSession?.invoiceId ? null : currentInvoiceSession?.sessionId}
        services={doctorServices}
        servicesLoading={servicesLoading}
        isMobile={isMobile}
        onSuccess={() => refetch()}
      />
    </div>
  );
};

export default SpecialistSessionsScreen;
